/**
 * Organization-scoping filter helper for AppSync list queries.
 *
 * Report, Project and QuizzesResult carry an `organizationID` field; other child records
 * (ActionItems, ChartData, Highlights, Categories, Statements) isolate transitively
 * through their parent. This helper is therefore intended for queries against models
 * that DO have `organizationID` (Reports/Projects/QuizzesResult), or any future org-scoped
 * read. For QuizzesResult the ownership field is `user_sub` (pass ownerField: 'user_sub').
 *
 * Backward compatibility is the key concern: legacy rows created before org support may
 * have a null/absent `organizationID`. Filtering strictly by org would HIDE that data.
 * So by default this builds an OR-clause that also matches legacy rows (no org) owned by
 * the current user — keeping them visible until a backfill stamps their organizationID.
 *
 * Once the backfill is verified, set REACT_APP_ENFORCE_ORG_SCOPE=true (or pass
 * `enforce: true`) to switch to a strict `organizationID == orgId` filter. The flag makes
 * rollback instant: flip it off and legacy rows reappear via the OR-clause.
 */

const ENFORCE_DEFAULT = process.env.REACT_APP_ENFORCE_ORG_SCOPE === 'true';

/**
 * Combine a caller's filter with an organization-scoping clause.
 *
 * @param {object|undefined} baseFilter - The caller's existing filter (may be undefined).
 * @param {string|undefined} orgId - The active organization id. If falsy, baseFilter is
 *        returned unchanged (caller decides how to handle "no active org").
 * @param {object} [options]
 * @param {string|null} [options.ownerSub=null] - The current user's id. When provided (and
 *        not enforcing), legacy rows with no organizationID owned by this user stay visible.
 * @param {string} [options.ownerField='user_sub'] - The ownership field on the model
 *        (e.g. 'user_sub' for Report, 'owner' for Project).
 * @param {object|null} [options.ownerCondition=null] - Full ownership condition to use in the
 *        legacy clause instead of `{ [ownerField]: { eq: ownerSub } }`. Use for non-eq matches,
 *        e.g. `{ assignedMembers: { contains: userSub } }`.
 * @param {boolean} [options.enforce=ENFORCE_DEFAULT] - Strict org-only filtering when true.
 * @returns {object|undefined} A filter suitable for `variables.filter`.
 */
/**
 * Attribute an org-less, `user_sub`-keyed record (a QuizzesResult) to an organization.
 *
 * This is the client-side guard that actually isolates quiz results across a user's orgs,
 * because the server filter alone cannot: QuizzesResult rows are keyed only by user_sub, and
 * DEFAULT/global learnings expose the SAME quiz id in every org — so a result earned on a
 * global quiz in one org matches the same global quiz shown in another. Mirrors the proven
 * `belongsToOrg` check in LearningProgressAdmin (the Learning Analytics page) so the dashboard
 * and learnings list scope quiz data identically:
 *   - a STAMPED result (has organizationID — new web results) must match the active org;
 *   - a LEGACY result (no organizationID — pre-stamping web + mobile, which never stamps)
 *     counts only if it was created AFTER the user joined THIS org. A user can't have taken an
 *     in-org quiz before joining, so this keeps their real pre-stamping history visible in the
 *     org where they earned it while stopping it from leaking into a newly-created org.
 *
 * Deliberately does NOT hide data on the "no active org" or "unknown join date" paths — it
 * returns true — because the earlier strict `organizationID == orgId` filter hid users' entire
 * pre-stamping history. Isolation is achieved without dropping legitimately-owned legacy rows.
 *
 * @param {{organizationID?: string, createdAt?: string}} result
 * @param {string|undefined} orgId - active organization id.
 * @param {number} [joinedAtMs=0] - epoch ms the user joined this org (OrganizationMember.createdAt).
 * @returns {boolean}
 */
export const resultBelongsToOrg = (result, orgId, joinedAtMs = 0) => {
  if (!orgId) return true; // no active org → don't hide anything
  if (result.organizationID) return result.organizationID === orgId;
  return new Date(result.createdAt || 0).getTime() >= (joinedAtMs || 0);
};

/**
 * Epoch ms the given user joined an organization = their OrganizationMember.createdAt for that
 * org (org creation time for the owner, who gets an ADMIN member row at creation). Used as the
 * cutoff by {@link resultBelongsToOrg} for legacy, unstamped quiz results.
 *
 * Fails OPEN (returns 0) when there is no active org, no member row, or the lookup errors, so a
 * transient failure never hides a user's data — it only relaxes the legacy-row cutoff.
 *
 * @param {object} API - the aws-amplify API module.
 * @param {string} listOrganizationMembers - the graphql listOrganizationMembers query string.
 * @param {string|undefined} orgId
 * @param {string|undefined} userSub
 * @returns {Promise<number>}
 */
export const getMemberJoinedAt = async (API, listOrganizationMembers, orgId, userSub) => {
  if (!orgId || !userSub) return 0;
  try {
    const res = await API.graphql({
      query: listOrganizationMembers,
      variables: {
        filter: { organizationID: { eq: orgId }, userSub: { eq: userSub }, _deleted: { ne: true } },
        limit: 10,
      },
    });
    const rows = res?.data?.listOrganizationMembers?.items || [];
    const times = rows
      .map((m) => (m.createdAt ? new Date(m.createdAt).getTime() : 0))
      .filter(Boolean);
    return times.length ? Math.min(...times) : 0; // earliest join if duplicate rows
  } catch (e) {
    console.warn('getMemberJoinedAt failed; not applying legacy date cutoff:', e);
    return 0; // fail open — never hide data on a lookup error
  }
};

export const withOrgFilter = (baseFilter, orgId, options = {}) => {
  const {
    ownerSub = null,
    ownerField = 'user_sub',
    ownerCondition = null,
    enforce = ENFORCE_DEFAULT,
  } = options;

  // No active org → don't constrain; let the caller decide.
  if (!orgId) return baseFilter || undefined;

  const ownerMatch = ownerCondition || (ownerSub ? { [ownerField]: { eq: ownerSub } } : null);
  const legacyClause = ownerMatch
    ? {
        and: [
          { organizationID: { attributeExists: false } },
          ownerMatch,
        ],
      }
    : { organizationID: { attributeExists: false } };

  const orgClause = enforce
    ? { organizationID: { eq: orgId } }
    : { or: [{ organizationID: { eq: orgId } }, legacyClause] };

  const hasBase = baseFilter && Object.keys(baseFilter).length > 0;
  return hasBase ? { and: [baseFilter, orgClause] } : orgClause;
};

export default withOrgFilter;
